/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.LocaleManager
import i18n.StringKey
import i18n.tr
import org.w3c.dom.HTMLElement
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlin.math.round
import models.Transaction
import utils.CategoryUtils

/**
 * Convert a PlaidTransaction to shared Transaction model using SERVER categories.
 * This replaces client-side AI categorization with the server's hybrid categorization.
 */
private fun PlaidTransaction.toSharedTransaction(userId: String): Transaction {
    val serverCategory = this.personal_finance_category
    val serverSource = this.category_source

    // Use server categories if available, otherwise fall back to basic Plaid category
    val categoryValue = serverCategory?.let {
        utils.CategoryUtils.formatTwoLevelCategoryLocalized(it.primary, it.detailed)
    } ?: this.category?.firstOrNull() ?: "Other"

    val primaryCategory = serverCategory?.primary ?: this.category?.firstOrNull() ?: "Other"
    val detailedCategory = serverCategory?.detailed ?: ""
    val confidence = when (serverSource) {
        "pattern", "cache", "llm" -> "95"
        "plaid" -> "90"
        else -> serverCategory?.confidence_level ?: "75"
    }

    return Transaction(
        transactionId = this.transaction_id,
        accountId = this.account_id,
        userId = userId,
        amount = this.amount,
        date = this.date,
        name = this.name,
        merchantName = this.name,
        category = categoryValue,
        personalFinanceCategoryPrimary = primaryCategory,
        personalFinanceCategoryDetailed = detailedCategory,
        personalFinanceCategoryConfidence = confidence,
        pending = false,
        parentTransactionId = this.parent_transaction_id,
        childType = this.child_type?.let { runCatching { models.ChildType.valueOf(it) }.getOrNull() },
        isSplitParent = this.is_split_parent,
        isDistributedParent = this.is_distributed_parent,
        splitOrder = this.split_order
    )
}

// Web-specific data classes for budget calculations
data class WebCategorySpending(
    val category: String,
    val amount: Double,
    val transactionCount: Int,
    val confidenceAverage: Double
)

data class WebBudgetSummary(
    val totalIncome: Double,
    val totalExpenses: Double,
    val netAmount: Double,
    val categorySpending: List<WebCategorySpending>,
    val period: String,
    val transactionCount: Int,
    val averageTransactionAmount: Double
)

data class WebSpendingProjection(
    val currentMonthSpending: Double,
    val projectedMonthlySpending: Double,
    val remainingDays: Int,
    val dailyAverageSpending: Double,
    val trendDirection: String,
    val categoryTrends: List<WebCategoryTrend>,
    val confidenceLevel: Double
)

data class WebCategoryTrend(
    val category: String,
    val currentAmount: Double,
    val projectedAmount: Double,
    val trend: String,
    val confidence: Double
)

// Extension function for formatting currency in JS
fun Double.formatCurrency(): String {
    return "$${kotlin.math.round(this).toLong()}"
}

// Data class for date ranges
data class DateRange(val start: kotlin.js.Date, val end: kotlin.js.Date)

// Helper function to calculate date ranges for time navigation
fun getDateRange(timeIntervalMode: String, periodOffset: Int): DateRange {
    val today = kotlin.js.Date()
    
    return when (timeIntervalMode) {
        "Weekly" -> {
            // Get start of current week (Sunday)
            val dayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, etc.
            val startOfThisWeek = kotlin.js.Date(today.getFullYear(), today.getMonth(), today.getDate() - dayOfWeek)
            
            // Apply offset (in weeks) using date manipulation instead of milliseconds
            val targetWeek = kotlin.js.Date(startOfThisWeek.getFullYear(), startOfThisWeek.getMonth(), startOfThisWeek.getDate() + (periodOffset * 7))
            val endOfWeek = kotlin.js.Date(targetWeek.getFullYear(), targetWeek.getMonth(), targetWeek.getDate() + 6)
            
            // Normalize to midnight to match iOS fix
            val startAtMidnight = kotlin.js.Date(targetWeek.getFullYear(), targetWeek.getMonth(), targetWeek.getDate())
            val endAtMidnight = kotlin.js.Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate(), 23, 59, 59, 999)
            
            DateRange(startAtMidnight, endAtMidnight)
        }
        "Monthly" -> {
            // Apply offset (in months)
            val targetMonth = kotlin.js.Date(today.getFullYear(), today.getMonth() + periodOffset, 1)
            val endOfMonth = kotlin.js.Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0, 23, 59, 59, 999)
            
            // Normalize to midnight
            val startAtMidnight = kotlin.js.Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1)
            
            DateRange(startAtMidnight, endOfMonth)
        }
        "Quarterly" -> {
            // Calculate current quarter
            val currentMonth = today.getMonth() // 0-based
            val quarterStartMonth = (currentMonth / 3) * 3 // 0, 3, 6, or 9

            // Apply offset (in quarters)
            val targetQuarter = kotlin.js.Date(today.getFullYear(), quarterStartMonth + (periodOffset * 3), 1)
            val endOfQuarter = kotlin.js.Date(targetQuarter.getFullYear(), targetQuarter.getMonth() + 3, 0, 23, 59, 59, 999)

            // Normalize to midnight
            val startAtMidnight = kotlin.js.Date(targetQuarter.getFullYear(), targetQuarter.getMonth(), 1)

            DateRange(startAtMidnight, endOfQuarter)
        }
        "Yearly" -> {
            // Apply offset (in years)
            val targetYear = today.getFullYear() + periodOffset
            val startOfYear = kotlin.js.Date(targetYear, 0, 1) // January 1st
            val endOfYear = kotlin.js.Date(targetYear, 11, 31, 23, 59, 59, 999) // December 31st

            DateRange(startOfYear, endOfYear)
        }
        else -> {
            // Default to current month
            val startOfMonth = kotlin.js.Date(today.getFullYear(), today.getMonth(), 1)
            val endOfMonth = kotlin.js.Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999)
            DateRange(startOfMonth, endOfMonth)
        }
    }
}

// Helper function to get formatted date range labels  
fun getFormattedDateRange(timeIntervalMode: String, periodOffset: Int): String {
    val today = kotlin.js.Date()
    
    return when (timeIntervalMode) {
        "Weekly" -> {
            if (periodOffset == 0) {
                "This Week"
            } else {
                // Calculate ISO week (Monday-based) to match shared Kotlin logic
                val dayOfWeek = today.getDay() // 0=Sunday, 1=Monday, ..., 6=Saturday
                val daysFromMonday = if (dayOfWeek == 0) 6 else dayOfWeek - 1 // Convert to ISO: 0=Monday
                val startOfThisWeek = kotlin.js.Date(today.getFullYear(), today.getMonth(), today.getDate() - daysFromMonday)
                val targetWeek = kotlin.js.Date(startOfThisWeek.getFullYear(), startOfThisWeek.getMonth(), startOfThisWeek.getDate() + (periodOffset * 7))
                val endOfWeek = kotlin.js.Date(targetWeek.getFullYear(), targetWeek.getMonth(), targetWeek.getDate() + 6)

                val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun",
                                       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

                val startMonth = monthNames[targetWeek.getMonth()]
                val endMonth = monthNames[endOfWeek.getMonth()]
                val startDay = targetWeek.getDate()
                val endDay = endOfWeek.getDate()
                val year = endOfWeek.getFullYear()

                if (startMonth == endMonth) {
                    "$startMonth $startDay to $endDay\n$year"
                } else {
                    "$startMonth $startDay to $endMonth $endDay\n$year"
                }
            }
        }
        "Monthly" -> {
            if (periodOffset == 0) {
                "This Month"
            } else {
                val startOfThisMonth = kotlin.js.Date(today.getFullYear(), today.getMonth(), 1)
                val targetMonth = kotlin.js.Date(startOfThisMonth.getFullYear(), startOfThisMonth.getMonth() + periodOffset, 1)
                
                val monthNames = arrayOf("January", "February", "March", "April", "May", "June", 
                                       "July", "August", "September", "October", "November", "December")
                
                val month = monthNames[targetMonth.getMonth()]
                val year = targetMonth.getFullYear()
                
                "$month $year"
            }
        }
        "Quarterly" -> {
            if (periodOffset == 0) {
                "This Quarter"
            } else {
                val currentMonth = today.getMonth() + 1 // getMonth() returns 0-11
                val currentQuarter = ((currentMonth - 1) / 3) + 1
                val targetQuarter = currentQuarter + periodOffset
                val year = today.getFullYear() + kotlin.math.floor((targetQuarter - 1) / 4.0).toInt()
                val quarter = ((targetQuarter - 1) % 4) + 1

                "Q$quarter\n$year"
            }
        }
        "Yearly" -> {
            if (periodOffset == 0) {
                "This Year"
            } else {
                val targetYear = today.getFullYear() + periodOffset
                "$targetYear"
            }
        }
        else -> "This Month"
    }
}

// Helper function to get period key for grouping transactions
fun getPeriodKey(date: String, timeInterval: String): String {
    // Date format is "YYYY-MM-DD"
    val parts = date.split("-")
    if (parts.size != 3) return date

    val year = parts[0].toIntOrNull() ?: return date
    val month = parts[1].toIntOrNull() ?: return date
    val day = parts[2].toIntOrNull() ?: return date

    val jsDate = kotlin.js.Date(year, month - 1, day) // JS months are 0-indexed

    return when (timeInterval) {
        "Weekly" -> {
            // Get ISO week number (Monday-based)
            val dayOfWeek = jsDate.getDay() // 0=Sunday, 1=Monday, ..., 6=Saturday
            val daysFromMonday = if (dayOfWeek == 0) 6 else dayOfWeek - 1
            val startOfWeek = kotlin.js.Date(year, month - 1, day - daysFromMonday)
            val weekYear = startOfWeek.getFullYear()
            val weekMonth = startOfWeek.getMonth() + 1
            val weekDay = startOfWeek.getDate()
            "$weekYear-${weekMonth.toString().padStart(2, '0')}-${weekDay.toString().padStart(2, '0')}"
        }
        "Monthly" -> {
            "$year-${month.toString().padStart(2, '0')}"
        }
        "Quarterly" -> {
            val quarter = ((month - 1) / 3) + 1
            "$year-Q$quarter"
        }
        "Yearly" -> {
            year.toString()
        }
        else -> date // Daily (or unknown)
    }
}

// Helper function to get formatted label for period
fun getPeriodLabel(periodKey: String, timeInterval: String): String {
    return when (timeInterval) {
        "Weekly" -> {
            // periodKey format: "YYYY-MM-DD" (start of week)
            val parts = periodKey.split("-")
            if (parts.size == 3) {
                val year = parts[0].toIntOrNull() ?: return periodKey
                val month = parts[1].toIntOrNull() ?: return periodKey
                val day = parts[2].toIntOrNull() ?: return periodKey
                val startDate = kotlin.js.Date(year, month - 1, day)
                val endDate = kotlin.js.Date(year, month - 1, day + 6)

                val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun",
                                       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
                val startMonth = monthNames[startDate.getMonth()]
                val endMonth = monthNames[endDate.getMonth()]
                val startDay = startDate.getDate()
                val endDay = endDate.getDate()

                if (startMonth == endMonth) {
                    "$startMonth $startDay-$endDay"
                } else {
                    "$startMonth $startDay - $endMonth $endDay"
                }
            } else {
                periodKey
            }
        }
        "Monthly" -> {
            // periodKey format: "YYYY-MM"
            val parts = periodKey.split("-")
            if (parts.size == 2) {
                val year = parts[0]
                val month = parts[1].toIntOrNull() ?: return periodKey
                val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun",
                                       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
                "${monthNames[month - 1]} $year"
            } else {
                periodKey
            }
        }
        "Quarterly" -> {
            // periodKey format: "YYYY-QN"
            periodKey
        }
        "Yearly" -> {
            // periodKey format: "YYYY"
            periodKey
        }
        else -> periodKey
    }
}

/**
 * Web Charts/Analysis Component with Real Data
 */
class WebChartsComponent(
    private val navigationManager: WebNavigationManager,
    private val accountId: String? = null
) {
    private var timeIntervalMode = "Monthly"
    private var periodOffset = 0
    private var container: HTMLElement? = null
    private var selectedChartType = "pie"
    private var selectedCategoryIndex: Int? = null
    private var currentBudgetSummary: WebBudgetSummary? = null
    private var selectedCategory: String? = null
    private var availableCategories: List<String> = emptyList()

    init {
        // Load from localStorage
        val storage = kotlinx.browser.localStorage
        timeIntervalMode = storage.getItem("time_interval_mode") ?: "Monthly"
        periodOffset = storage.getItem("period_offset")?.toIntOrNull() ?: 0
        selectedCategory = storage.getItem("selected_category") // null = "All"
        selectedChartType = storage.getItem("selected_chart_type") ?: "charts" // Persist chart type preference
    }
    
    fun setTimeIntervalMode(mode: String) {
        timeIntervalMode = mode
        periodOffset = 0 // Reset to current period when changing mode
        save()
    }
    
    fun setPeriodOffset(offset: Int) {
        periodOffset = offset
        save()
    }
    
    fun goToPrevious() {
        console.log("⬅️ NAV: goToPrevious() called, periodOffset changing from $periodOffset to ${periodOffset - 1}")
        periodOffset--
        save()
    }

    fun goToNext() {
        console.log("➡️ NAV: goToNext() called, periodOffset changing from $periodOffset to ${periodOffset + 1}")
        periodOffset++
        save()
    }
    
    fun goToToday() {
        periodOffset = 0
        save()
    }
    
    fun getCurrentPeriod(): String {
        return getFormattedDateRange(timeIntervalMode, periodOffset)
    }
    
    private fun save() {
        val storage = kotlinx.browser.localStorage
        storage.setItem("time_interval_mode", timeIntervalMode)
        storage.setItem("period_offset", periodOffset.toString())
        if (selectedCategory != null) {
            storage.setItem("selected_category", selectedCategory!!)
        } else {
            storage.removeItem("selected_category")
        }
    }

    fun setSelectedCategory(category: String?) {
        selectedCategory = category
        save()
    }

    private fun sanitizeCategoryId(category: String): String {
        // Replace all non-alphanumeric characters with underscores
        return category.replace(Regex("[^a-zA-Z0-9]"), "_")
    }

    private fun createCategorySelectionButtons(): String {
        if (selectedChartType != "trend" || availableCategories.isEmpty()) {
            return ""
        }

        val buttonsHtml = buildString {
            // "All" button
            val isAllSelected = selectedCategory == null
            append("""
                <button id="categoryAllBtn" style="
                    padding: 8px 16px;
                    margin: 0 4px 4px 0;
                    border: 1px solid ${if (isAllSelected) "#00d9ff" else "#333"};
                    background: ${if (isAllSelected) "#00d9ff" else "#141414"};
                    color: ${if (isAllSelected) "#0a0a0a" else "#888"};
                    border-radius: 20px;
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: ${if (isAllSelected) "bold" else "normal"};
                ">All</button>
            """)

            // Primary category buttons
            availableCategories.forEach { category ->
                val isSelected = selectedCategory == category
                val sanitizedId = sanitizeCategoryId(category)
                append("""
                    <button id="category${sanitizedId}Btn" data-category="$category" style="
                        padding: 8px 16px;
                        margin: 0 4px 4px 0;
                        border: 1px solid ${if (isSelected) "#00d9ff" else "#333"};
                        background: ${if (isSelected) "#00d9ff" else "#141414"};
                        color: ${if (isSelected) "#0a0a0a" else "#888"};
                        border-radius: 20px;
                        cursor: pointer;
                        font-size: 12px;
                        font-weight: ${if (isSelected) "bold" else "normal"};
                    ">$category</button>
                """)
            }
        }

        return """
            <div id="categorySelection" style="margin-bottom: 16px; padding: 12px; background: #141414; border-radius: 12px; border: 1px solid #1f1f1f;">
                <h4 style="margin: 0 0 8px 0; color: #888; font-size: 14px;">Filter by Category:</h4>
                <div style="display: flex; flex-wrap: wrap;">
                    $buttonsHtml
                </div>
            </div>
        """
    }

    private fun updateCategoryButtons(container: HTMLElement) {
        // Re-render the category selection area
        val categoryArea = container.querySelector("#categorySelection")
        if (categoryArea != null) {
            categoryArea.outerHTML = createCategorySelectionButtons()
            setupCategoryEventListeners(container)
        }
    }

    private fun setupCategoryEventListeners(container: HTMLElement) {
        console.log("🔍 TRACE: setupCategoryEventListeners called (DIFFERENT FUNCTION)")
        // "All" button
        container.querySelector("#categoryAllBtn")?.addEventListener("click", {
            console.log("🔍 TRACE: setupCategoryEventListeners - 'All' button clicked")
            setSelectedCategory(null)
            // Re-load charts with all categories
            loadChartsData(container)
        })

        // Individual category buttons
        availableCategories.forEach { category ->
            val sanitizedId = sanitizeCategoryId(category)
            val buttonId = "#category${sanitizedId}Btn"
            container.querySelector(buttonId)?.addEventListener("click", {
                console.log("🔍 TRACE: setupCategoryEventListeners - Category button '$category' clicked")
                setSelectedCategory(category)
                // Re-load charts with selected category
                loadChartsData(container)
            })
        }
    }

    fun render(): HTMLElement {
        val chartsContainer = document.createElement("div") as HTMLElement
        chartsContainer.className = "charts-container"
        container = chartsContainer

        // Create permanent structure with separate areas for navigation and content
        chartsContainer.innerHTML = """
            <div style="background: transparent; min-height: 100vh;">
                <!-- Slim top nav bar -->
                <div style="display: flex; align-items: center; padding: 12px 32px; background: rgba(10, 10, 10, 0.92); border-bottom: 1px solid #1a1a1a; position: sticky; top: 0; z-index: 100;">
                    <div id="webcharts-logo" style="font-size: 18px; font-weight: 700; letter-spacing: -0.5px; margin-right: 40px; display: flex; align-items: center; cursor: pointer;" title="${StringKey.TOOLTIP_GO_HOME.tr()}">
                        <span style="color: #00d9ff;">wiz</span>
                        <span style="display: flex; flex-direction: column; gap: 2px; margin: 0 1px; padding: 2px 0;">
                            <span style="width: 10px; height: 2px; background: #00d9ff; border-radius: 1px;"></span>
                            <span style="width: 10px; height: 2px; background: #00d9ff; border-radius: 1px;"></span>
                            <span style="width: 10px; height: 2px; background: #00d9ff; border-radius: 1px;"></span>
                        </span>
                        <span style="color: #00d9ff;">r</span>
                    </div>
                    <div style="display: flex; gap: 8px; flex: 1;">
                        <span id="nav-activity" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: #666;">${StringKey.NAV_TRANSACTIONS.tr()}</span>
                        <span id="nav-dashboard" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: #666;">${StringKey.NAV_DASHBOARD.tr()}</span>
                        <span id="nav-analysis" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: #1a1a1a; color: #ffffff;">Analysis</span>
                        <span id="nav-goals" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: #666;">${StringKey.NAV_GOALS.tr()}</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 12px;">
                        <span id="nav-language" style="padding: 4px 10px; border-radius: 4px; background: #1a1a1a; color: #00d9ff; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid #333; transition: all 0.2s;" title="${StringKey.TOOLTIP_CHANGE_LANGUAGE.tr()}">${languageToggleLabel()}</span>
                        <span id="nav-settings" style="width: 32px; height: 32px; border-radius: 50%; background: #1a1a1a; display: flex; align-items: center; justify-content: center; font-size: 16px; cursor: pointer; border: 1px solid #333;">&#9776;</span>
                    </div>
                </div>

                <!-- Main content -->
                <div style="padding: 24px 32px;">
                    <h1 style="color: #ffffff; margin-bottom: 20px; font-size: 28px; font-weight: 600;">Analysis${if (accountId != null) " (Account View)" else ""}</h1>

                    <!-- Time Interval Mode Selector -->
                    <div style="margin-bottom: 20px;">
                        ${createIntervalModeSelector()}
                    </div>

                    <!-- Time Navigation -->
                    ${createTimeNavigationBar()}

                    <!-- Chart Type Selector -->
                    <div style="display: flex; gap: 10px; margin-bottom: 20px; background: #141414; padding: 15px; border-radius: 12px; border: 1px solid #1f1f1f;">
                        <button id="chartsBtn" style="
                            flex: 1;
                            padding: 10px;
                            border: 2px solid ${if (selectedChartType == "charts") "#00d9ff" else "#333"};
                            background: ${if (selectedChartType == "charts") "rgba(0, 217, 255, 0.15)" else "#141414"};
                            color: ${if (selectedChartType == "charts") "#00d9ff" else "#888"};
                            border-radius: 8px;
                            cursor: pointer;
                            font-weight: ${if (selectedChartType == "charts") "bold" else "normal"};
                        ">📊 Charts</button>
                        <button id="trendChartBtn" style="
                            flex: 1;
                            padding: 10px;
                            border: 2px solid ${if (selectedChartType == "trend") "#00d9ff" else "#333"};
                            background: ${if (selectedChartType == "trend") "rgba(0, 217, 255, 0.15)" else "#141414"};
                            color: ${if (selectedChartType == "trend") "#00d9ff" else "#888"};
                            border-radius: 8px;
                            cursor: pointer;
                            font-weight: ${if (selectedChartType == "trend") "bold" else "normal"};
                        ">📈 Trends</button>
                    </div>

                    <!-- Category Selection (shown only for trend charts) -->
                    ${createCategorySelectionButtons()}

                    <!-- Dynamic Chart Container -->
                    <div id="chartContainer" style="background: #141414; padding: 20px; border-radius: 12px; border: 1px solid #1f1f1f; margin-bottom: 20px; width: 100%;">
                        <!-- Total Expenses Display -->
                        <div id="totalExpensesDisplay" style="text-align: center; margin-bottom: 10px;">
                            <h3 id="categoryLabel" style="color: #888; margin: 0; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px;">Total Expenses</h3>
                            <p id="totalExpensesAmount" style="font-size: 28px; font-weight: bold; color: #ff4757; margin: 5px 0;">Loading...</p>
                        </div>
                        <!-- Charts Layout (will be populated dynamically) -->
                        <div id="chartsLayout"></div>
                    </div>

                    <!-- Chart Legend Below Chart -->
                    <div id="chartLegend" style="background: #141414; padding: 15px; border-radius: 12px; border: 1px solid #1f1f1f; margin-top: 10px;">
                        <!-- Legend will be populated dynamically -->
                    </div>
                </div>
            </div>
        """

        // Load real data asynchronously
        loadChartsData(chartsContainer)

        // Add event listeners for time navigation and chart selection
        setupEventListeners(chartsContainer)

        // Add navigation event listeners
        setupNavListeners(chartsContainer)

        return chartsContainer
    }

    private fun setupNavListeners(container: HTMLElement) {
        container.querySelector("#nav-activity")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
        })
        container.querySelector("#nav-dashboard")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.DASHBOARD)
        })
        container.querySelector("#nav-analysis")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.PROJECTIONS)
        })
        container.querySelector("#nav-goals")?.addEventListener("click", {
            navigationManager.navigateTo(Screen.SavingsGoals(openCreateDialog = false))
        })
        // Language toggle - quick switch between languages
        container.querySelector("#nav-language")?.addEventListener("click", {
            LocaleManager.toggleLocale()
            window.location.reload()
        })
    }

    private fun createIntervalModeSelector(): String {
        return """
            <div style="position: relative;">
                <select id="intervalModeSelect" style="
                    padding: 8px 12px;
                    border: 1px solid #333;
                    border-radius: 6px;
                    background: #141414;
                    color: #ffffff;
                    cursor: pointer;
                    font-size: 14px;
                ">
                    <option value="Weekly" ${if (timeIntervalMode == "Weekly") "selected" else ""}>${StringKey.TIME_WEEKLY.tr()}</option>
                    <option value="Monthly" ${if (timeIntervalMode == "Monthly") "selected" else ""}>${StringKey.TIME_MONTHLY.tr()}</option>
                    <option value="Quarterly" ${if (timeIntervalMode == "Quarterly") "selected" else ""}>${StringKey.TIME_QUARTERLY.tr()}</option>
                </select>
            </div>
        """
    }

    private fun createTimeNavigationBar(): String {
        val currentPeriod = getCurrentPeriod()
        val currentOffset = periodOffset

        return """
            <div id="timeNavigationBar" style="display: flex; gap: 12px; margin-bottom: 20px; align-items: center;">
                <button id="prevButton" style="
                    flex: 1;
                    padding: 12px;
                    border: 1px solid #333;
                    background: #141414;
                    color: #00d9ff;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    transition: background 0.2s;
                " onmouseover="this.style.background='#1f1f1f'" onmouseout="this.style.background='#141414'">
                    Prev
                </button>

                <div style="flex: 2; text-align: center; position: relative;">
                    <button id="currentPeriodButton" style="
                        width: 100%;
                        padding: ${if (currentOffset == 0) "12px" else "8px"};
                        border: none;
                        background: ${if (currentOffset == 0) "#00d9ff" else "#0099b3"};
                        color: ${if (currentOffset == 0) "#0a0a0a" else "white"};
                        border-radius: 8px;
                        cursor: ${if (currentOffset == 0) "default" else "pointer"};
                        font-size: 14px;
                        font-weight: 600;
                        line-height: 1.2;
                        white-space: pre-line;
                    ">
                        ${currentPeriod.replace("\\n", "\n")}
                    </button>
                    ${if (currentOffset != 0) """
                        <button id="todayButton" style="
                            position: absolute;
                            top: 100%;
                            left: 50%;
                            transform: translateX(-50%);
                            margin-top: 4px;
                            padding: 4px 8px;
                            border: none;
                            background: rgba(255,255,255,0.1);
                            color: #888;
                            border-radius: 4px;
                            cursor: pointer;
                            font-size: 12px;
                            " onmouseover="this.style.background='rgba(255,255,255,0.15)'" onmouseout="this.style.background='rgba(255,255,255,0.1)'">
                            Today
                        </button>
                    """ else ""}
                </div>

                <button id="nextButton" style="
                    flex: 1;
                    padding: 12px;
                    border: 1px solid #333;
                    background: #141414;
                    color: #00d9ff;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    transition: background 0.2s;
                " onmouseover="this.style.background='#1f1f1f'" onmouseout="this.style.background='#141414'">
                    Next
                </button>
            </div>
        """
    }

    private fun setupEventListeners(container: HTMLElement) {
        console.log("🛠️ SETUP: setupEventListeners called")

        // Logo click - navigate to Landing page
        container.querySelector("#webcharts-logo")?.addEventListener("click", {
            navigationManager.navigateTo(Screen.Landing)
        })

        // Chart type selection
        container.querySelector("#chartsBtn")?.addEventListener("click", {
            selectedChartType = "charts"
            kotlinx.browser.localStorage.setItem("selected_chart_type", selectedChartType)
            refreshCharts()
        })

        container.querySelector("#trendChartBtn")?.addEventListener("click", {
            selectedChartType = "trend"
            kotlinx.browser.localStorage.setItem("selected_chart_type", selectedChartType)
            refreshCharts()
        })

        // Time navigation
        container.querySelector("#intervalModeSelect")?.addEventListener("change", { event ->
            val select = event.target as? org.w3c.dom.HTMLSelectElement
            select?.value?.let { mode ->
                setTimeIntervalMode(mode)
                refreshCharts()
            }
        })

        // Debug navigation buttons
        val prevButton = container.querySelector("#prevButton")
        val nextButton = container.querySelector("#nextButton")
        console.log("🛠️ SETUP: Found prevButton = $prevButton")
        console.log("🛠️ SETUP: Found nextButton = $nextButton")
        
        container.querySelector("#prevButton")?.addEventListener("click", {
            console.log("🔥 BUTTON CLICK: Prev button clicked!")
            goToPrevious()
            refreshCharts()
        })

        container.querySelector("#nextButton")?.addEventListener("click", {
            console.log("🔥 BUTTON CLICK: Next button clicked!")
            goToNext()
            refreshCharts()
        })
        
        container.querySelector("#currentPeriodButton")?.addEventListener("click", {
            if (periodOffset != 0) {
                goToToday()
                refreshCharts()
            }
        })
        
        container.querySelector("#todayButton")?.addEventListener("click", {
            goToToday()
            refreshCharts()
        })

        // Category selection (for trend charts)
        setupCategoryEventListeners(container)
    }
    
    private fun refreshCharts() {
        container?.let {
            // Update the time navigation bar
            val timeNavBar = it.querySelector("#timeNavigationBar")
            if (timeNavBar != null && timeNavBar is HTMLElement) {
                timeNavBar.outerHTML = createTimeNavigationBar()
                // Re-attach event listeners after updating HTML
                setupEventListeners(it)
            }

            // Update chart type buttons
            updateChartTypeButtons(it)

            // Update the interval selector
            val intervalSelect = it.querySelector("#intervalModeSelect") as? org.w3c.dom.HTMLSelectElement
            intervalSelect?.value = timeIntervalMode

            // Update current period on existing trend chart if navigation occurred
            val trendChartInstance = window.asDynamic().trendChartInstance
            val monthlyData = window.asDynamic().monthlyData
            if (trendChartInstance != null && monthlyData != null) {
                try {
                    // Check if monthlyData is an array and has elements
                    if (js("Array.isArray").call(null, monthlyData) == true && monthlyData.length > 0) {
                        // Calculate new current month index based on periodOffset
                        val baseCurrentIndex = (monthlyData.length as Int) - 1
                        val newCurrentIndex = (baseCurrentIndex + periodOffset).coerceIn(0, baseCurrentIndex)

                        console.log("🌐 WEB NAVIGATION: Updating chart to show period offset $periodOffset, new current index: $newCurrentIndex")

                        // Build arrays safely from dynamic data
                        val monthlyValues = DoubleArray(monthlyData.length as Int) { index ->
                            val item = monthlyData[index]
                            val value = if (js("Array.isArray").call(null, item) == true) {
                                (item[1] as? Number)?.toDouble() ?: 0.0
                            } else {
                                // Handle object format
                                val secondProp = item.asDynamic().second
                                if (secondProp != undefined) {
                                    (secondProp as? Number)?.toDouble() ?: 0.0
                                } else 0.0
                            }
                            value
                        }

                        val monthlyLabels = Array(monthlyData.length as Int) { index ->
                            val item = monthlyData[index]
                            if (js("Array.isArray").call(null, item) == true) {
                                item[0].toString()
                            } else {
                                // Handle object format
                                val firstProp = item.asDynamic().first
                                if (firstProp != undefined) {
                                    firstProp.toString()
                                } else "Month ${index + 1}"
                            }
                        }

                        // 🔍 DEBUG: Log navigation details
                        console.log("🚀 NAVIGATION DEBUG: Button clicked, periodOffset=$periodOffset")
                        console.log("🚀 NAVIGATION DEBUG: Old currentIndex=$baseCurrentIndex, New currentIndex=$newCurrentIndex")
                        console.log("🚀 NAVIGATION DEBUG: Monthly values array:", monthlyValues)
                        console.log("🚀 NAVIGATION DEBUG: Value at new index: ${monthlyValues[newCurrentIndex]}")

                        // Update chart with new current period (emphasis is handled automatically)
                        trendChartInstance.updateCurrentPeriod(newCurrentIndex, monthlyValues, monthlyLabels)
                    }
                } catch (e: Exception) {
                    console.error("Error updating trend chart during navigation:", e)
                }
            }

            // Reload the data
            loadChartsData(it)
        }
    }


    private fun updateChartTypeButtons(container: HTMLElement) {
        val chartsBtn = container.querySelector("#chartsBtn") as? HTMLElement
        val trendBtn = container.querySelector("#trendChartBtn") as? HTMLElement

        // Reset all buttons
        listOf(chartsBtn, trendBtn).forEach { btn ->
            btn?.style?.border = "2px solid #333"
            btn?.style?.background = "#141414"
            btn?.style?.color = "#888"
            btn?.style?.fontWeight = "normal"
        }

        // Highlight selected button
        when (selectedChartType) {
            "charts" -> chartsBtn?.apply {
                style.border = "2px solid #00d9ff"
                style.background = "rgba(0, 217, 255, 0.15)"
                style.color = "#00d9ff"
                style.fontWeight = "bold"
            }
            "trend" -> trendBtn?.apply {
                style.border = "2px solid #00d9ff"
                style.background = "rgba(0, 217, 255, 0.15)"
                style.color = "#00d9ff"
                style.fontWeight = "bold"
            }
        }
    }
    
    private fun loadChartsData(container: HTMLElement) {
        console.log("🔍 TRACE: loadChartsData called with selectedCategory = $selectedCategory")
        GlobalScope.launch {
            try {
                val apiService = ApiService()
                
                // Try to get real transactions from server
                val result = try {
                    apiService.getTransactionsFromServer()
                } catch (e: Exception) {
                    console.error("Failed to fetch real transactions for charts:", e)
                    null
                }
                
                if (result != null && result.transactions.isNotEmpty()) {
                    val budgetSummary = calculateBudgetFromTransactions(result.transactions)

                    // Extract and populate available categories for filtering
                    availableCategories = budgetSummary.categorySpending.map { it.category }.distinct().sorted()

                    drawChart(container, budgetSummary)
                    updateLegend(container, budgetSummary)
                    updateCategoryButtons(container)
                } else {
                    // No data available - show empty chart
                    val emptySummary = WebBudgetSummary(
                        totalIncome = 0.0,
                        totalExpenses = 0.0,
                        netAmount = 0.0,
                        categorySpending = emptyList(),
                        period = "No data",
                        transactionCount = 0,
                        averageTransactionAmount = 0.0
                    )
                    drawChart(container, emptySummary)
                    updateLegend(container, emptySummary)
                }
            } catch (e: Exception) {
                console.error("Error loading charts data:", e)
            }
        }
    }
    
    private fun calculateBudgetFromTransactions(transactions: List<PlaidTransaction>): WebBudgetSummary {
        // Apply proper date filtering based on time selection
        val dateRange = getDateRange(timeIntervalMode, periodOffset)
        val sharedTransactions = transactions.filter { transaction ->
            // Parse date as local date (not UTC) to avoid timezone shift
            val dateParts = transaction.date.split("-")
            val year = dateParts[0].toInt()
            val month = dateParts[1].toInt() - 1 // JavaScript months are 0-based
            val day = dateParts[2].toInt()
            val transactionDate = kotlin.js.Date(year, month, day)
            
            val isInDateRange = transactionDate.getTime() >= dateRange.start.getTime() && transactionDate.getTime() <= dateRange.end.getTime()
            
            // Filter by account if selected
            val isAccountMatch = accountId == null || transaction.account_id == accountId
            
            isInDateRange && isAccountMatch
        }.map { plaidTx ->
            // Use SERVER categories instead of client-side AI
            plaidTx.toSharedTransaction("web_user").copy(
                amount = -plaidTx.amount // Reverse Plaid signs
            )
        }
        
        // Manual calculation grouped by primary categories (like iOS/Android)
        val expenseTransactions = sharedTransactions.filter { it.amount < 0 && !it.excludeFromTotals }
        val incomeTransactions = sharedTransactions.filter { it.amount > 0 }
        
        // Group by primary category (categoryLevel1)
        val categoryMap = mutableMapOf<String, MutableList<Transaction>>()
        expenseTransactions.forEach { transaction ->
            val primaryCategory = transaction.personalFinanceCategoryPrimary?.takeIf { it.isNotBlank() } ?: "Other"
            categoryMap.getOrPut(primaryCategory) { mutableListOf() }.add(transaction)
        }
        
        val categorySpending = categoryMap.map { (category, transactions) ->
            val totalAmount = transactions.sumOf { kotlin.math.abs(it.amount) }
            val avgConfidence = transactions.mapNotNull { 
                it.personalFinanceCategoryConfidence?.toDoubleOrNull() 
            }.average().takeIf { !it.isNaN() } ?: 0.0
            
            WebCategorySpending(
                category = category,
                amount = totalAmount,
                transactionCount = transactions.size,
                confidenceAverage = avgConfidence
            )
        }.sortedByDescending { it.amount }
        
        val totalIncome = incomeTransactions.sumOf { it.amount }
        val totalExpenses = expenseTransactions.sumOf { kotlin.math.abs(it.amount) }
        val netAmount = totalIncome - totalExpenses
        val avgTransactionAmount = sharedTransactions.takeIf { it.isNotEmpty() }?.let { 
            it.sumOf { kotlin.math.abs(it.amount) } / it.size 
        } ?: 0.0
        
        return WebBudgetSummary(
            totalIncome = totalIncome,
            totalExpenses = totalExpenses,
            netAmount = netAmount,
            categorySpending = categorySpending,
            period = getCurrentPeriod(),
            transactionCount = sharedTransactions.size,
            averageTransactionAmount = avgTransactionAmount
        )
    }
    
    private fun drawChart(container: HTMLElement, budgetSummary: WebBudgetSummary) {
        console.log("🔍 TRACE: drawChart called with selectedChartType = $selectedChartType, selectedCategory = $selectedCategory")
        // Store current budget summary
        currentBudgetSummary = budgetSummary
        
        // Update Total Expenses display with category filtering
        val totalExpensesElement = container.querySelector("#totalExpensesAmount") as? HTMLElement
        val displayAmount = if (selectedCategory != null && selectedChartType == "trend") {
            // Calculate filtered amount for selected category from categorySpending
            val categoryAmount = budgetSummary.categorySpending.find { it.category == selectedCategory }?.amount ?: 0.0
            kotlin.math.abs(categoryAmount)
        } else {
            kotlin.math.abs(budgetSummary.totalExpenses)
        }
        totalExpensesElement?.textContent = displayAmount.formatCurrency()

        // Update category label in header
        val categoryLabelElement = container.querySelector("#categoryLabel") as? HTMLElement
        categoryLabelElement?.textContent = if (selectedCategory != null && selectedChartType == "trend") {
            "$selectedCategory Expenses"
        } else {
            "Total Expenses"
        }
        
        when (selectedChartType) {
            "charts" -> {
                drawCombinedCharts(container, budgetSummary)
                return // Exit early since we handle the layout differently
            }
            "trend" -> {
                drawTrendLayout(container, budgetSummary)
                return
            }
        }
    }
    
    private fun drawPieChart(ctx: org.w3c.dom.CanvasRenderingContext2D, canvas: org.w3c.dom.HTMLCanvasElement, summary: WebBudgetSummary) {
        val centerX = canvas.width / 2.0
        val centerY = canvas.height / 2.0
        val outerRadius = minOf(canvas.width, canvas.height) / 3.0
        val innerRadius = outerRadius * 0.5 // Create donut hole (50% of outer radius)
        
        val colors = arrayOf(
            "#E91E63", "#2196F3", "#4CAF50", "#FF9800", 
            "#9C27B0", "#795548", "#00BCD4", "#8BC34A", "#607D8B"
        )
        
        var currentAngle = -kotlin.math.PI / 2 // Start at top
        
        summary.categorySpending.forEachIndexed { index, category ->
            val percentage = kotlin.math.abs(category.amount) / summary.totalExpenses
            val sweepAngle = percentage * 2 * kotlin.math.PI
            
            // Determine if this slice is selected and adjust radius for pop-out effect
            val isSelected = selectedCategoryIndex == index
            val baseOuterRadius = if (isSelected) outerRadius * 1.1 else outerRadius
            val baseInnerRadius = if (isSelected) innerRadius * 1.1 else innerRadius
            val sliceCenterX = if (isSelected) {
                val midAngle = currentAngle + sweepAngle / 2
                centerX + kotlin.math.cos(midAngle) * 10
            } else centerX
            val sliceCenterY = if (isSelected) {
                val midAngle = currentAngle + sweepAngle / 2
                centerY + kotlin.math.sin(midAngle) * 10
            } else centerY
            
            // Draw donut slice
            ctx.beginPath()
            ctx.arc(sliceCenterX, sliceCenterY, baseOuterRadius, currentAngle, currentAngle + sweepAngle)
            ctx.arc(sliceCenterX, sliceCenterY, baseInnerRadius, currentAngle + sweepAngle, currentAngle, true)
            ctx.closePath()
            
            // Apply color with opacity for non-selected slices
            val color = colors[index % colors.size]
            ctx.fillStyle = if (selectedCategoryIndex == null || isSelected) {
                color
            } else {
                "${color}80" // Add 50% opacity to non-selected slices
            }
            ctx.fill()
            
            // Draw border for selected slice
            if (isSelected) {
                ctx.strokeStyle = "#333"
                ctx.lineWidth = 2.0
                ctx.stroke()
            }
            
            // Draw label
            val labelAngle = currentAngle + sweepAngle / 2
            val labelRadius = if (isSelected) baseOuterRadius * 0.7 else baseOuterRadius * 0.7
            val labelX = sliceCenterX + kotlin.math.cos(labelAngle) * labelRadius
            val labelY = sliceCenterY + kotlin.math.sin(labelAngle) * labelRadius
            
            ctx.fillStyle = "white"
            ctx.font = if (isSelected) "14px Arial bold" else "12px Arial"
            ctx.textAlign = "center".asDynamic()
            ctx.textBaseline = "middle".asDynamic()
            val percentText = "${(percentage * 100).toInt()}%"
            ctx.fillText(percentText, labelX, labelY)
            
            currentAngle += sweepAngle
        }
    }
    
    private fun drawBarChart(ctx: org.w3c.dom.CanvasRenderingContext2D, canvas: org.w3c.dom.HTMLCanvasElement, summary: WebBudgetSummary) {
        val padding = 40.0
        val chartWidth = canvas.width - padding * 2
        val chartHeight = canvas.height - padding * 2

        val categories = summary.categorySpending // Show all categories
        if (categories.isEmpty()) return

        val maxAmount = categories.maxOf { it.amount }
        val barWidth = chartWidth / categories.size * 0.7
        val spacing = chartWidth / categories.size * 0.3

        val colors = arrayOf(
            "#E91E63", "#2196F3", "#4CAF50", "#FF9800",
            "#9C27B0", "#795548", "#00BCD4", "#8BC34A", "#607D8B"
        )

        // Draw bars with selection effects
        categories.forEachIndexed { index, category ->
            val isSelected = selectedCategoryIndex == index
            val barHeight = (category.amount / maxAmount) * chartHeight
            val x = padding + index * (barWidth + spacing) + spacing / 2
            val y = canvas.height - padding - barHeight

            // Apply color with opacity for non-selected bars (matching pie chart)
            val color = colors[index % colors.size]
            ctx.fillStyle = if (selectedCategoryIndex == null || isSelected) {
                color
            } else {
                "${color}80" // Add 50% opacity to non-selected bars
            }

            // Draw bar with slight enlargement if selected
            val displayBarWidth = if (isSelected) barWidth * 1.05 else barWidth
            val displayX = if (isSelected) x - (displayBarWidth - barWidth) / 2 else x
            ctx.fillRect(displayX, y, displayBarWidth, barHeight)

            // Draw border for selected bar
            if (isSelected) {
                ctx.strokeStyle = "#333"
                ctx.lineWidth = 3.0
                ctx.strokeRect(displayX, y, displayBarWidth, barHeight)
            }

            // Draw amount on top of bar
            ctx.fillStyle = "#333"
            ctx.font = if (isSelected) "14px Arial bold" else "12px Arial"
            ctx.textAlign = "center".asDynamic()
            ctx.fillText(
                "$${category.amount.toInt()}",
                x + barWidth / 2,
                y - 5
            )

            // Draw category name below
            ctx.save()
            ctx.translate(x + barWidth / 2, canvas.height - padding + 15)
            ctx.rotate(-kotlin.math.PI / 4)
            ctx.fillStyle = if (isSelected) "#333" else "#666"
            ctx.font = if (isSelected) "11px Arial bold" else "10px Arial"
            ctx.textAlign = "right".asDynamic()
            ctx.fillText(category.category, 0.0, 0.0)
            ctx.restore()
        }
    }
    
    private fun drawCombinedCharts(container: HTMLElement, summary: WebBudgetSummary) {
        // Sync selectedCategory (from Trends page) to selectedCategoryIndex (for Charts page)
        if (selectedCategory != null && selectedCategoryIndex == null) {
            selectedCategoryIndex = summary.categorySpending.indexOfFirst { it.category == selectedCategory }
            if (selectedCategoryIndex == -1) selectedCategoryIndex = null
        }

        // Create side-by-side layout for pie and bar charts with proper canvas sizing
        val chartsLayout = container.querySelector("#chartsLayout") ?: return
        
        chartsLayout.innerHTML = """
            <div style="display: flex; gap: 20px; align-items: flex-start; justify-content: center; padding: 20px;">
                <div style="flex: 1; max-width: 400px;">
                    <h3 style="text-align: center; margin-bottom: 10px; color: #333;">Expense Categories</h3>
                    <canvas id="pieChart" width="400" height="400" style="border: 1px solid #ddd; border-radius: 8px;"></canvas>
                </div>
                <div style="flex: 1; max-width: 400px;">
                    <h3 style="text-align: center; margin-bottom: 10px; color: #333;">Monthly Comparison</h3>
                    <canvas id="barChart" width="400" height="400" style="border: 1px solid #ddd; border-radius: 8px;"></canvas>
                </div>
            </div>
        """
        
        // Render pie chart
        val pieCanvas = chartsLayout.querySelector("#pieChart") as? org.w3c.dom.HTMLCanvasElement
        val pieCtx = pieCanvas?.getContext("2d") as? org.w3c.dom.CanvasRenderingContext2D
        if (pieCanvas != null && pieCtx != null) {
            drawPieChart(pieCtx, pieCanvas, summary)
            setupPieChartInteraction(pieCanvas, summary)
        }
        
        // Render bar chart
        val barCanvas = chartsLayout.querySelector("#barChart") as? org.w3c.dom.HTMLCanvasElement
        val barCtx = barCanvas?.getContext("2d") as? org.w3c.dom.CanvasRenderingContext2D
        if (barCanvas != null && barCtx != null) {
            drawBarChart(barCtx, barCanvas, summary)
            setupBarChartInteraction(barCanvas, summary)
        }
        
        // Update legend for the combined view
        updateLegend(container, summary)
    }
    
    private fun setupCategoryButtonListeners(container: HTMLElement) {
        console.log("🔍 TRACE: setupCategoryButtonListeners called")
        // Set up "All" button listener
        val allButton = container.querySelector("#categoryAllBtn") as? HTMLElement
        allButton?.addEventListener("click", {
            console.log("🔍 TRACE: 'All' button clicked")
            setSelectedCategory(null)
            currentBudgetSummary?.let { drawChart(container, it) }
        })

        // Set up individual category button listeners
        availableCategories.forEach { category ->
            val sanitizedId = sanitizeCategoryId(category)
            val buttonId = "category${sanitizedId}Btn"
            val button = container.querySelector("#$buttonId") as? HTMLElement
            button?.addEventListener("click", {
                console.log("🔍 TRACE: Category button '$category' clicked")
                setSelectedCategory(category)
                currentBudgetSummary?.let { drawChart(container, it) }
            })
        }
    }

    private fun drawTrendLayout(container: HTMLElement, summary: WebBudgetSummary) {
        console.log("🔍 TRACE: drawTrendLayout called with selectedCategory = $selectedCategory")
        // Create layout for trend chart with TrendChart.kt forecasting
        val chartsLayout = container.querySelector("#chartsLayout") ?: return

        chartsLayout.innerHTML = """
            <div style="padding: 20px; width: 100%; height: 100%; position: relative;">
                <h3 style="text-align: center; margin-bottom: 20px; color: #333;">Spending Trends</h3>
                ${createCategorySelectionButtons()}
                <div style="display: flex; justify-content: center; width: 100%; position: relative;">
                    <div id="trendChartContainer" style="width: 100%; max-width: 1400px; height: 600px; border: 1px solid #ddd; border-radius: 8px;"></div>
                </div>
            </div>
        """

        // Set up category button event listeners
        setupCategoryButtonListeners(container)

        // Load and render trend chart with TrendChart.kt component
        GlobalScope.launch {
            try {
                console.log("🔵 WEB Trend: Starting to load transaction data...")
                console.log("🔵 WEB Trend: timeIntervalMode = '$timeIntervalMode'")
                console.log("🔵 WEB Trend: selectedCategory = '$selectedCategory'")

                val apiService = ApiService()
                val result = apiService.getTransactionsFromServer()

                console.log("🔵 WEB Trend: Got ${result.transactions.size} transactions from API")

                // Show first 3 transactions for debugging
                result.transactions.take(3).forEach { tx ->
                    console.log("  Sample transaction: ${tx.name}, amount=${tx.amount}, date=${tx.date}")
                }

                if (result.transactions.isNotEmpty()) {
                    // Convert Plaid transactions to shared Transaction model using SERVER categories
                    console.log("🔵 WEB Trend: Converting ${result.transactions.size} Plaid transactions to shared Transaction model...")
                    val transactions = result.transactions.map { plaidTx ->
                        plaidTx.toSharedTransaction("current_user").copy(
                            amount = -plaidTx.amount // Reverse Plaid signs
                        )
                    }.filter { transaction ->
                        val match = accountId == null || transaction.accountId == accountId
                        if (!match && accountId != null) {
                             // console.log("🔍 WEB DEBUG: Filtered out transaction ${transaction.name} (Account: ${transaction.accountId} != Selected: $accountId)")
                        }
                        match
                    }
                    
                    console.log("🔍 WEB DEBUG: Selected Account ID: $accountId")
                    console.log("🔍 WEB DEBUG: Total transactions after filter: ${transactions.size}")
                    if (transactions.isNotEmpty()) {
                        console.log("🔍 WEB DEBUG: Sample transaction account ID: ${transactions.first().accountId}")
                    }

                    console.log("🔵 WEB Trend: Converted transactions. First 3:")
                    transactions.take(3).forEach { tx ->
                        console.log("  Transaction: ${tx.name}, amount=${tx.amount}, date=${tx.date}, category=${tx.personalFinanceCategoryPrimary}")
                    }

                    // Use shared analytics function with proper timeframe
                    console.log("🟢 WEB Trend: Calling applyAdvancedAnalyticsWithCategories with timeIntervalMode='$timeIntervalMode'")
                    val analyticsData = analytics.applyAdvancedAnalyticsWithCategories(
                        transactions = transactions,
                        timeIntervalMode = timeIntervalMode
                    )

                    console.log("🟢 WEB Trend: Analytics returned ${analyticsData.originalData.size} data points")
                    console.log("🟢 WEB Trend: Available categories: ${analyticsData.availableCategories.size}")

                    // Extract data from analytics result
                    val allData = analyticsData.originalData.map { pair ->
                        Pair(pair.first as String, pair.second as Double)
                    }

                    console.log("🔵 WEB Trend: Extracted ${allData.size} data points from analytics")
                    console.log("🔵 WEB Trend: First 5 periods from analytics:")
                    allData.take(5).forEach { (period, total) ->
                        console.log("    $period: $${total.asDynamic().toFixed(2)}")
                    }

                    // Apply category filtering if needed
                    val monthlyData = if (selectedCategory != null) {
                        console.log("🔍 WEB Trend: Filtering for category '$selectedCategory'")
                        val categoryData = analyticsData.categoryData[selectedCategory]
                        if (categoryData != null) {
                            console.log("🔍 WEB Trend: Found category data with ${categoryData.size} points")
                            categoryData.map { pair ->
                                Pair(pair.first as String, pair.second as Double)
                            }
                        } else {
                            console.log("🔴 WEB Trend: Category '$selectedCategory' not found in analytics data")
                            allData
                        }
                    } else {
                        console.log("🔵 WEB Trend: No category filter, using all data")
                        allData
                    }

                    console.log("🔵 WEB Trend: Final data for chart ($timeIntervalMode): ${monthlyData.size} data points")
                    console.log("🔵 WEB Trend: First 5 final periods:")
                    monthlyData.take(5).forEach { (period, total) ->
                        console.log("    $period: $${total.asDynamic().toFixed(2)}")
                    }

                    if (monthlyData.isNotEmpty()) {
                        val monthlyValues = monthlyData.map { it.second }.toDoubleArray()
                        val monthlyLabels = monthlyData.map { it.first }.toTypedArray()

                        // Calculate and display monthly average
                        val monthlyAverage = monthlyValues.sum() / monthlyValues.size
                        console.log("📊 Monthly Average: $${monthlyAverage.asDynamic().toFixed(2)}")

                        // Find current period index by looking for "This Week", "This Month", or "This Quarter"
                        val baseCurrentIndex = monthlyLabels.indexOfFirst { label ->
                            label == "This Week" || label == "This Month" || label == "This Quarter"
                        }.let { index ->
                            if (index >= 0) index else monthlyValues.size - 1 // Fallback to last item if not found
                        }
                        val currentMonthIndex = (baseCurrentIndex + periodOffset).coerceIn(0, monthlyValues.size - 1)

                        // Create and render TrendChart.kt with monthly data and current period
                        console.log("🚨 WEB: Creating Monthly TrendChart with ${monthlyValues.size} monthly data points, current month at index $currentMonthIndex (baseIndex=$baseCurrentIndex, periodOffset=$periodOffset)")

                        // Preserve method selection from existing instance if available
                        val previousMethodId = try {
                            (window.asDynamic().trendChartInstance as? TrendChart)?.getSelectedMethodId()
                        } catch (e: Exception) {
                            null
                        }
                        console.log("🔄 NAV: Preserving previous method selection: $previousMethodId")

                        val trendChart = TrendChart()
                        // Restore method selection before rendering if available
                        previousMethodId?.let {
                            trendChart.setSelectedMethod(it)
                            console.log("✅ NAV: Restored method selection to $it")
                        }
                        trendChart.render(
                            containerId = "trendChartContainer", 
                            data = monthlyValues, 
                            dates = monthlyLabels, 
                            currentPeriodIndex = currentMonthIndex,
                            timeInterval = timeIntervalMode
                        )

                        // Store the chart instance for navigation
                        window.asDynamic().trendChartInstance = trendChart
                        window.asDynamic().monthlyData = monthlyData

                        // Set initial amount display
                        updateCurrentPeriodDisplay(currentMonthIndex, monthlyData.toTypedArray())

                        // Scroll to show current month centered
                        kotlinx.browser.window.setTimeout({
                            trendChart.scrollToDataPoint(currentMonthIndex)
                        }, 200)

                        console.log("🚨 WEB: Monthly TrendChart.kt rendered successfully with forecasting!")
                    } else {
                        console.log("🔍 Trend chart: No monthly data found")
                        val container = kotlinx.browser.document.getElementById("trendChartContainer")
                        container?.innerHTML = "<div style='display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 16px;'>No expense data found</div>"
                    }
                } else {
                    console.log("🔍 Trend chart: No transactions available")
                    val container = kotlinx.browser.document.getElementById("trendChartContainer")
                    container?.innerHTML = "<div style='display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 16px;'>No transaction data available</div>"
                }
            } catch (e: Exception) {
                console.error("🔍 Trend chart error:", e.message)
                val container = kotlinx.browser.document.getElementById("trendChartContainer")
                container?.innerHTML = "<div style='display: flex; align-items: center; justify-content: center; height: 100%; color: #d32f2f; font-size: 16px;'>Chart error: ${e.message}</div>"
            }
        }
    }

    /**
     * Update the current period amount display above the navigation
     */
    private fun updateCurrentPeriodDisplay(periodIndex: Int, monthlyData: Array<Pair<String, Double>>) {
        val currentAmount = monthlyData.getOrNull(periodIndex)?.second ?: 0.0
        val currentLabel = monthlyData.getOrNull(periodIndex)?.first ?: "Unknown"

        val amountElement = document.getElementById("currentPeriodAmount")
        val labelElement = document.getElementById("currentPeriodLabel")

        if (amountElement != null && labelElement != null) {
            // Format amount without decimals and add visual emphasis
            val formattedAmount = if (currentAmount >= 0) {
                "+$${currentAmount.toInt()}"
            } else {
                "-$${(-currentAmount).toInt()}"
            }

            amountElement.textContent = formattedAmount
            labelElement.textContent = currentLabel

            // Add emphasis styling for current period
            val elementStyle = (amountElement as HTMLElement).style
            elementStyle.setProperty("transition", "all 0.3s ease")
            elementStyle.setProperty("transform", "scale(1.05)")
            elementStyle.setProperty("font-weight", "700")
            elementStyle.setProperty("color", if (currentAmount >= 0) "#4CAF50" else "#F44336")

            // Reset emphasis after animation
            js("setTimeout")({
                val elementStyleReset = (amountElement as HTMLElement).style
                elementStyleReset.setProperty("transform", "scale(1.0)")
            }, 300)

            console.log("🌐 WEB AMOUNT DISPLAY: Updated to show $formattedAmount for $currentLabel")
        }
    }

    
    private fun setupPieChartInteraction(canvas: org.w3c.dom.HTMLCanvasElement, summary: WebBudgetSummary) {
        canvas.onclick = { event ->
            val rect = canvas.getBoundingClientRect()
            val x = event.asDynamic().clientX - rect.left
            val y = event.asDynamic().clientY - rect.top
            
            val centerX = canvas.width / 2.0
            val centerY = canvas.height / 2.0
            val radius = minOf(canvas.width, canvas.height) / 3.0
            
            // Calculate distance from center
            val dx = x - centerX
            val dy = y - centerY
            val distance = kotlin.math.sqrt(dx * dx + dy * dy)
            
            if (distance <= radius) {
                // Calculate angle
                var angle = kotlin.math.atan2(dy, dx)
                // Normalize angle to start from top
                angle += kotlin.math.PI / 2
                if (angle < 0) angle += 2 * kotlin.math.PI
                
                // Find which slice was clicked
                var currentAngle = 0.0
                var foundSlice = false
                summary.categorySpending.forEachIndexed { index, category ->
                    if (foundSlice) return@forEachIndexed
                    
                    val percentage = category.amount / summary.totalExpenses
                    val sweepAngle = percentage * 2 * kotlin.math.PI
                    
                    if (angle >= currentAngle && angle < currentAngle + sweepAngle) {
                        // Toggle selection
                        selectedCategoryIndex = if (selectedCategoryIndex == index) null else index
                        // Sync with selectedCategory for Trends page
                        selectedCategory = selectedCategoryIndex?.let { summary.categorySpending.getOrNull(it)?.category }
                        // Redraw chart and update legend
                        container?.let { drawChart(it, summary) }
                        // Scroll to the selected category in legend
                        if (selectedCategoryIndex != null) {
                            scrollToLegendItem(selectedCategoryIndex!!)
                        }
                        foundSlice = true
                    }
                    currentAngle += sweepAngle
                }
            } else {
                // Click outside the pie - deselect
                selectedCategoryIndex = null
                selectedCategory = null
                container?.let { drawChart(it, summary) }
            }
        }
    }

    private fun setupBarChartInteraction(canvas: org.w3c.dom.HTMLCanvasElement, summary: WebBudgetSummary) {
        val padding = 40.0
        val chartWidth = canvas.width - padding * 2
        val categories = summary.categorySpending
        if (categories.isEmpty()) return

        val barWidth = chartWidth / categories.size * 0.7
        val spacing = chartWidth / categories.size * 0.3

        canvas.onclick = { event ->
            val rect = canvas.getBoundingClientRect()
            val clickX = event.asDynamic().clientX - rect.left
            val clickY = event.asDynamic().clientY - rect.top

            // Check which bar was clicked
            var foundBar = false
            categories.forEachIndexed { index, category ->
                if (foundBar) return@forEachIndexed

                val x = padding + index * (barWidth + spacing) + spacing / 2
                val maxAmount = categories.maxOf { it.amount }
                val barHeight = (category.amount / maxAmount) * (canvas.height - padding * 2)
                val y = canvas.height - padding - barHeight

                // Check if click is within this bar
                if (clickX >= x && clickX <= x + barWidth && clickY >= y && clickY <= canvas.height - padding) {
                    // Toggle selection
                    selectedCategoryIndex = if (selectedCategoryIndex == index) null else index
                    // Sync with selectedCategory for Trends page
                    selectedCategory = selectedCategoryIndex?.let { summary.categorySpending.getOrNull(it)?.category }
                    // Redraw chart and update legend
                    container?.let { drawChart(it, summary) }
                    // Scroll to the selected category in legend
                    if (selectedCategoryIndex != null) {
                        scrollToLegendItem(selectedCategoryIndex!!)
                    }
                    foundBar = true
                }
            }

            // If no bar was clicked, deselect
            if (!foundBar) {
                selectedCategoryIndex = null
                selectedCategory = null
                container?.let { drawChart(it, summary) }
            }
        }
    }

    fun setupTrendChartInteraction(
        canvas: org.w3c.dom.HTMLCanvasElement, 
        monthlyData: List<Pair<String, Double>>,
        padding: Double,
        xStep: Double, 
        chartHeight: Double,
        maxAmount: Double
    ) {
        console.log("🟡 WEB TOOLTIP DEBUG: setupTrendChartInteraction called with ${monthlyData.size} data points")
        val tooltipController = ui.TooltipController()
        canvas.onclick = { event ->
            val rect = canvas.getBoundingClientRect()
            val clickX = event.asDynamic().clientX - rect.left
            val clickY = event.asDynamic().clientY - rect.top
            val tapThreshold = 25.0 // Touch target size
            
            console.log("🟡 WEB TOOLTIP DEBUG: Click detected at ($clickX, $clickY)")
            console.log("🟡 WEB TOOLTIP DEBUG: Monthly data size: ${monthlyData.size}")
            
            // Find the closest data point
            var closestIndex = -1
            var closestDistance = Double.MAX_VALUE
            
            monthlyData.forEachIndexed { index, (_, amount) ->
                val pointX = padding + index * xStep
                val pointY = canvas.height - padding - (amount / maxAmount) * chartHeight
                val distance = kotlin.math.sqrt(
                    (clickX - pointX) * (clickX - pointX) + 
                    (clickY - pointY) * (clickY - pointY)
                )
                
                if (distance < closestDistance && distance < tapThreshold) {
                    closestDistance = distance
                    closestIndex = index
                }
            }
            
            if (closestIndex >= 0) {
                val dataPoint = monthlyData[closestIndex]
                val month = dataPoint.first
                val amount = dataPoint.second
                console.log("🟢 WEB TOOLTIP DEBUG: Hit detected on point $closestIndex ($month, $amount)")

                // Use shared TooltipController
                val timeSeriesPoint = ui.createTimeSeriesPoint(month, amount)
                val tooltipResult = tooltipController.showTooltip(
                    dataPoint = timeSeriesPoint,
                    categoryName = month,
                    x = clickX.unsafeCast<Float>(),
                    y = clickY.unsafeCast<Float>(),
                    screenWidth = canvas.width.unsafeCast<Float>(),
                    screenHeight = canvas.height.unsafeCast<Float>()
                )
                
                console.log("🟢 WEB TOOLTIP DEBUG: Tooltip result isVisible: ${tooltipResult.isVisible}")
                
                // Update HTML tooltip with TooltipController result
                if (tooltipResult.success && tooltipResult.isVisible && tooltipResult.tooltipInfo != null) {
                    val tooltipInfo = tooltipResult.tooltipInfo!!
                    showTrendTooltip(canvas, tooltipInfo.categoryName, tooltipInfo.formattedValue, tooltipResult.x.toDouble(), tooltipResult.y.toDouble())
                }
            } else {
                tooltipController.hideTooltip()
                hideTrendTooltip()
            }
        }
        
        // Hide tooltip when clicking outside canvas
        kotlinx.browser.document.addEventListener("click", { event ->
            val target = event.target as? org.w3c.dom.Element
            if (target != canvas) {
                hideTrendTooltip()
            }
        })
    }
    
    private fun showTrendTooltip(canvas: org.w3c.dom.HTMLCanvasElement, month: String, formattedValue: String, clickX: Double, clickY: Double) {
        val tooltip = kotlinx.browser.document.getElementById("trendTooltip") as? HTMLElement ?: return
        val labelElement = kotlinx.browser.document.getElementById("tooltipLabel") as? HTMLElement ?: return  
        val valueElement = kotlinx.browser.document.getElementById("tooltipValue") as? HTMLElement ?: return
        
        // Update tooltip content using TooltipController formatting
        labelElement.textContent = month
        valueElement.textContent = formattedValue
        
        // Position tooltip using coordinates from TooltipController (already adjusted for boundaries)
        val rect = canvas.getBoundingClientRect()
        val tooltipX = (rect.left + clickX)
            .coerceAtLeast(10.0)
            .coerceAtMost(kotlinx.browser.window.innerWidth - 120.0)
        val tooltipY = (rect.top + clickY)
            .coerceAtLeast(10.0)
            .coerceAtMost(kotlinx.browser.window.innerHeight - 60.0)
        
        tooltip.style.left = "${tooltipX}px"
        tooltip.style.top = "${tooltipY}px"
        tooltip.style.display = "block"
    }
    
    private fun hideTrendTooltip() {
        val tooltip = kotlinx.browser.document.getElementById("trendTooltip") as? HTMLElement ?: return
        tooltip.style.display = "none"
    }
    
    private fun scrollToLegendItem(index: Int) {
        val legendItem = document.getElementById("legend-item-$index") as? HTMLElement
        legendItem?.scrollIntoView(object {
            val behavior = "smooth"
            val block = "center"
        }.asDynamic())
    }
    
    private fun updateLegend(container: HTMLElement, summary: WebBudgetSummary) {
        val legendContainer = container.querySelector("#chartLegend") as? HTMLElement ?: return
        
        val colors = arrayOf(
            "#E91E63", "#2196F3", "#4CAF50", "#FF9800", 
            "#9C27B0", "#795548", "#00BCD4", "#8BC34A", "#607D8B"
        )
        
        val legendHtml = """
            <h3 style="margin: 0 0 15px 0; color: #333;">Category Breakdown</h3>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px;">
                ${summary.categorySpending.mapIndexed { index, category ->
                    val percentage = (category.amount / summary.totalExpenses * 100).toInt()
                    val isSelected = selectedCategoryIndex == index
                    val backgroundColor = if (selectedCategoryIndex == null || isSelected) "white" else "#f5f5f5"
                    val opacity = if (selectedCategoryIndex == null || isSelected) "1.0" else "0.6"
                    val border = if (isSelected) "2px solid #2196F3" else "1px solid #eee"
                    val boxShadow = if (isSelected) "0 4px 8px rgba(0,0,0,0.15)" else "0 1px 3px rgba(0,0,0,0.1)"
                    """
                    <div id="legend-item-$index" style="
                        display: flex; 
                        align-items: center; 
                        gap: 10px; 
                        padding: 12px;
                        background: $backgroundColor;
                        border: $border;
                        border-radius: 8px;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        opacity: $opacity;
                        box-shadow: $boxShadow;
                    " data-category-index="$index">
                        <div style="width: 20px; height: 20px; background: ${colors[index % colors.size]}; border-radius: 4px;"></div>
                        <div style="flex: 1;">
                            <div style="font-size: ${if (isSelected) "15px" else "14px"}; color: #333; font-weight: ${if (isSelected) "bold" else "normal"};">${category.category}</div>
                            <div style="font-size: 12px; color: #666;">${category.amount.formatCurrency()} ($percentage%)</div>
                        </div>
                    </div>
                    """
                }.joinToString("")}
            </div>
        """
        
        legendContainer.innerHTML = legendHtml
        
        // Add click listeners to legend items
        summary.categorySpending.forEachIndexed { index, _ ->
            val legendItem = document.getElementById("legend-item-$index")
            legendItem?.addEventListener("click", {
                // Toggle selection
                selectedCategoryIndex = if (selectedCategoryIndex == index) null else index
                // Sync with selectedCategory for Trends page
                selectedCategory = selectedCategoryIndex?.let { summary.categorySpending.getOrNull(it)?.category }
                // Redraw chart and update legend
                currentBudgetSummary?.let { drawChart(container, it) }
            })
        }
    }

    /**
     * Render trend chart using pre-computed analytics data - OPTIMIZED
     */
    private fun renderTrendChartWithAnalytics(analyticsData: analytics.AdvancedAnalyticsData) {
        console.log("🌐 WEB TREND CHART: Using pre-computed analytics data with ${analyticsData.originalData.size} historical points")

        try {
            // Extract historical data and dates for the chart
            val dataPoints = analyticsData.originalData.map { it.second }.toDoubleArray()
            val dataPointDates = analyticsData.originalData.mapIndexed { index, timeSeriesPoint ->
                // Simple fallback - use index-based dates for now
                "Day ${index + 1}"
            }.toTypedArray()
            console.log("🌐 WEB TREND CHART: Data points: ${dataPoints.take(5).joinToString()}...")

            if (dataPoints.isNotEmpty()) {
                // Create and render TrendChart.kt with the analytics data and dates
                // Preserve method selection from existing instance if available
                val previousMethodId = try {
                    (window.asDynamic().trendChartInstance as? TrendChart)?.getSelectedMethodId()
                } catch (e: Exception) {
                    null
                }
                console.log("🔄 ANALYTICS: Preserving previous method selection: $previousMethodId")

                val trendChart = TrendChart()
                // Restore method selection before rendering if available
                previousMethodId?.let {
                    trendChart.setSelectedMethod(it)
                    console.log("✅ ANALYTICS: Restored method selection to $it")
                }
                trendChart.render("trendChartContainer", dataPoints, dataPointDates)

                // Auto-scroll to center the selected period
                val selectedPeriodLabel = utils.DateUtils.calculatePeriodLabel(timeIntervalMode, periodOffset)
                val selectedIndex = analyticsData.originalData.indexOfFirst { it.first == selectedPeriodLabel }
                if (selectedIndex >= 0) {
                    kotlinx.browser.window.setTimeout({
                        trendChart.scrollToDataPoint(selectedIndex)
                        console.log("📍 WEB SCROLL: Scrolling to center period '$selectedPeriodLabel' at index $selectedIndex")
                    }, 200)
                }

                // Store the chart instance for navigation
                window.asDynamic().trendChartInstance = trendChart

                console.log("🌐 WEB TREND CHART: Successfully rendered TrendChart.kt with ${dataPoints.size} data points and ${analyticsData.forecast.size} forecast points")
            } else {
                console.log("🌐 WEB TREND CHART: No data points in analytics data")
                val container = kotlinx.browser.document.getElementById("trendChartContainer")
                container?.innerHTML = "<div style='display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 16px;'>No historical data available</div>"
            }
        } catch (e: Exception) {
            console.error("🌐 WEB TREND CHART ERROR: ${e.message}")
            val container = kotlinx.browser.document.getElementById("trendChartContainer")
            container?.innerHTML = "<div style='display: flex; align-items: center; justify-content: center; height: 100%; color: #d32f2f; font-size: 16px;'>Failed to render chart: ${e.message}</div>"
        }
    }
}

/**
 * Shows a Pro upgrade prompt modal overlay.
 * Displayed when a non-Pro user encounters a Pro-gated feature.
 */
fun showProUpgradeModal(container: org.w3c.dom.Element, onUpgrade: () -> Unit) {
    val overlay = document.createElement("div") as HTMLElement
    overlay.style.cssText = """
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.72);
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
    """.trimIndent()

    val panel = document.createElement("div") as HTMLElement
    panel.style.cssText = """
        background: #1a1a1a;
        border-radius: 16px;
        padding: 32px;
        max-width: 400px;
        text-align: center;
        border: 1px solid #333;
    """.trimIndent()

    panel.innerHTML = """
        <h3 style="color:#fff;margin:0 0 12px;">${StringKey.PRO_UPGRADE_PROMPT_TITLE.tr()}</h3>
        <p style="color:#999;margin:0 0 24px;">${StringKey.PRO_UPGRADE_PROMPT_MESSAGE.tr()}</p>
        <div id="pro-upgrade-cta" style="background:#00d9ff;color:#000;padding:14px 24px;border-radius:10px;font-weight:700;cursor:pointer;margin-bottom:12px;">${StringKey.PRO_UPGRADE_PROMPT_CTA.tr()}</div>
        <div id="pro-upgrade-dismiss" style="color:#666;cursor:pointer;font-size:14px;">${StringKey.ACTION_CANCEL.tr()}</div>
    """.trimIndent()

    overlay.appendChild(panel)
    container.appendChild(overlay)

    panel.querySelector("#pro-upgrade-cta")?.addEventListener("click", {
        overlay.remove()
        onUpgrade()
    })
    panel.querySelector("#pro-upgrade-dismiss")?.addEventListener("click", {
        overlay.remove()
    })
    // Click on backdrop (outside panel) dismisses
    overlay.addEventListener("click", { e ->
        if (e.target == overlay) overlay.remove()
    })
    // Clicks inside panel don't bubble to backdrop
    panel.addEventListener("click", { e ->
        e.stopPropagation()
    })
}

